import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  CheckCircle,
  CloudUpload,
  Delete,
  Download,
} from '@mui/icons-material';
import {
  Attachment,
  AttachmentKind,
  MedicalRecord,
  attachmentDownloadUrl,
  confirmPayment,
  deleteAttachment,
  getAttachments,
  uploadAttachment,
} from '../api/api';

interface Props {
  record: MedicalRecord | null;
  open: boolean;
  onClose: () => void;
  /** Called after a change that affects the parent list (payment confirmed). */
  onChanged: () => void;
}

/**
 * The document kinds the clinic attaches to a surgery, labelled as staff
 * refer to them rather than by their database identifiers.
 */
const ATTACHMENT_KINDS: { value: AttachmentKind; label: string; hint: string }[] = [
  { value: 'etiqueta', label: 'Etiqueta', hint: 'Material/implant label' },
  { value: 'folha_sala', label: 'Folha de sala', hint: 'Folha de reserva' },
  { value: 'tcle', label: 'TCLE', hint: 'Termo de consentimento' },
  { value: 'relatorio', label: 'Relatório', hint: 'Surgery report (more than one allowed)' },
  { value: 'outro', label: 'Outro', hint: 'Anything else' },
];

const formatBRL = (value: number | null | undefined): string =>
  value == null
    ? '-'
    : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatBytes = (bytes: number | null): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Record detail: confirm payment, and attach the surgery paperwork.
 *
 * Both actions had working endpoints and no interface at all, so neither was
 * reachable without calling the API directly.
 */
const RecordDetailDialog: React.FC<Props> = ({ record, open, onClose, onChanged }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [kind, setKind] = useState<AttachmentKind>('relatorio');
  const [amountReceived, setAmountReceived] = useState<string>('');
  const [receivedAt, setReceivedAt] = useState<string>('');

  const loadAttachments = useCallback(async () => {
    if (!record) return;
    setLoading(true);
    try {
      setAttachments(await getAttachments(record.id));
    } catch {
      setError('Could not load attachments');
    } finally {
      setLoading(false);
    }
  }, [record]);

  useEffect(() => {
    if (open && record) {
      setError(null);
      setSuccess(null);
      // Default the amount to the doctor's net share of what was billed --
      // the figure most likely to be correct, still editable.
      setAmountReceived(
        record.amount_received != null
          ? String(record.amount_received)
          : record.amount_billed != null
            ? String(record.amount_billed)
            : ''
      );
      setReceivedAt('');
      loadAttachments();
    }
  }, [open, record, loadAttachments]);

  const handleConfirmPayment = async () => {
    if (!record) return;
    const amount = parseFloat(amountReceived);
    if (Number.isNaN(amount) || amount < 0) {
      setError('Enter a valid amount received');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await confirmPayment(record.id, amount, receivedAt || undefined);
      setSuccess(
        `Pagamento confirmado: ${formatBRL(result.amount_received)}` +
          (result.payment_lag_days != null ? ` · ${result.payment_lag_days} dias até receber` : '')
      );
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Could not confirm payment');
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !record) return;
    setBusy(true);
    setError(null);
    try {
      await uploadAttachment(record.id, kind, file);
      setSuccess(`${file.name} anexado`);
      await loadAttachments();
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Upload failed');
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (attachment: Attachment) => {
    setBusy(true);
    try {
      await deleteAttachment(attachment.id);
      await loadAttachments();
    } catch {
      setError('Could not delete attachment');
    } finally {
      setBusy(false);
    }
  };

  if (!record) return null;

  const kindLabel = (value: AttachmentKind) =>
    ATTACHMENT_KINDS.find((k) => k.value === value)?.label ?? value;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {record.patient_name || 'Registro'}
        <Typography variant="body2" color="text.secondary">
          {record.record_date} · {record.doctor_name || 'sem médico'} ·{' '}
          {record.hospital_name || 'sem hospital'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* ---- Billing ---- */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Faturamento</Typography>
        <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Valor faturado (bruto)</Typography>
            <Typography fontWeight={600}>{formatBRL(record.amount_billed)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Líquido do médico</Typography>
            <Typography fontWeight={600}>{formatBRL(record.amount_billed_net)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Recebido</Typography>
            <Typography fontWeight={600}>{formatBRL(record.amount_received)}</Typography>
          </Box>
          {record.payment_lag_days != null && (
            <Box>
              <Typography variant="caption" color="text.secondary">Tempo até receber</Typography>
              <Typography fontWeight={600}>{record.payment_lag_days} dias</Typography>
            </Box>
          )}
        </Stack>

        {record.payment_confirmed ? (
          <Chip
            icon={<CheckCircle />}
            color="success"
            label={`Pago em ${record.payment_received_at ?? '—'}`}
            sx={{ mb: 2 }}
          />
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <TextField
              label="Valor recebido"
              size="small"
              value={amountReceived}
              onChange={(e) => setAmountReceived(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
            />
            <TextField
              label="Data do recebimento"
              size="small"
              type="date"
              value={receivedAt}
              onChange={(e) => setReceivedAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Vazio = hoje"
            />
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={handleConfirmPayment}
              disabled={busy}
            >
              Confirmar pagamento
            </Button>
          </Stack>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ---- Attachments ---- */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Documentos ({attachments.length})
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={kind}
              label="Tipo"
              onChange={(e) => setKind(e.target.value as AttachmentKind)}
            >
              {ATTACHMENT_KINDS.map((k) => (
                <MenuItem key={k.value} value={k.value}>
                  <Tooltip title={k.hint} placement="right">
                    <span>{k.label}</span>
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <input
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/heic,image/tiff"
            onChange={handleUpload}
            disabled={busy}
            style={{ display: 'none' }}
            id="attachment-upload"
          />
          <label htmlFor="attachment-upload">
            <Button
              component="span"
              variant="outlined"
              startIcon={busy ? <CircularProgress size={18} /> : <CloudUpload />}
              disabled={busy}
            >
              Anexar
            </Button>
          </label>
          <Typography variant="caption" color="text.secondary">
            PDF ou imagem, até 25 MB
          </Typography>
        </Stack>

        {loading ? (
          <CircularProgress size={22} />
        ) : attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Nenhum documento anexado.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {attachments.map((a) => (
              <Stack
                key={a.id}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <AttachFile fontSize="small" color="action" />
                <Chip size="small" label={kindLabel(a.kind)} />
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                  {a.original_filename}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatBytes(a.size_bytes)}
                </Typography>
                <Tooltip title="Baixar">
                  <IconButton
                    size="small"
                    component="a"
                    href={attachmentDownloadUrl(a.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Download fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Remover">
                  <IconButton size="small" onClick={() => handleDelete(a)} disabled={busy}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecordDetailDialog;
